import { useState } from 'react';
import {
  Card,
  Separator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui';
import { FileText, ExternalLink } from 'lucide-react';

interface VendorOverviewProps {
  vendor: {
    name: string;
    status: 'active' | 'inactive';
    contactDate?: string;
    submittedAt?: string;
    primaryContact: string;
    primaryEmail: string;
    summary: string;
  };
  showReviewerAssignment?: boolean;
  showSubmittedDocuments?: boolean;
  programContext?: boolean; // Whether this is shown in context of a specific program
}

export function VendorOverview({ 
  vendor, 
  showReviewerAssignment = true,
  showSubmittedDocuments = true,
  programContext = true 
}: VendorOverviewProps) {
  const [selectedReviewer, setSelectedReviewer] = useState<string>('');

  const reviewers = [
    'Sarah Johnson - Technical Lead',
    'Michael Chen - Procurement Officer',
    'Emily Rodriguez - Compliance Specialist',
    'David Park - Project Manager',
    'Jessica Williams - Contract Analyst',
  ];

  return (
    <div className="grid grid-cols-2 gap-6">
      {/* Left Column */}
      <div className="space-y-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">
            Details
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-600 dark:text-slate-400 mb-1 uppercase tracking-wider">
                  Company Name
                </p>
                <p className="text-sm text-slate-900 dark:text-slate-50">{vendor.name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-600 dark:text-slate-400 mb-1 uppercase tracking-wider">
                  Status
                </p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  vendor.status === 'active' 
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`}>
                  {vendor.status}
                </span>
              </div>
            </div>
            {programContext && vendor.submittedAt && (
              <>
                <Separator className="bg-slate-200 dark:bg-slate-700" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mb-1 uppercase tracking-wider">
                      RFP Submitted At
                    </p>
                    <p className="text-sm text-slate-900 dark:text-slate-50">
                      {vendor.submittedAt}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mb-1 uppercase tracking-wider">
                      Primary Contact
                    </p>
                    <p className="text-sm text-slate-900 dark:text-slate-50">
                      {vendor.primaryContact}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      {vendor.primaryEmail}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">
            Summary
          </h3>
          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            {vendor.summary}
          </p>
        </Card>

        {/* Past Program Considerations */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">
            Consideration in Past Programs
          </h3>
          <div className="space-y-3">
            {[
              {
                id: 'prev-1',
                programName: 'RFP-2023-015: Autonomous Ground Vehicle Initiative',
                status: 'Not Selected',
                stage: 'Final Selection',
                date: 'Mar 2024',
                reason: 'Strong technical capability but focused on aerial systems',
                technicalScore: 4.5,
              },
              {
                id: 'prev-2',
                programName: 'RFP-2023-008: ISR Platform Development',
                status: 'Finalist',
                stage: 'Diligence & Contracting',
                date: 'Nov 2023',
                reason: 'Advanced to final negotiations, withdrew due to capacity constraints',
                technicalScore: 4.8,
              },
            ].map((program, idx) => (
              <div
                key={idx}
                className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors cursor-pointer"
                onClick={() => {
                  // In real app, would navigate to that program's vendor evaluation
                  console.log(`Navigate to program ${program.id}`)
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-50">
                        {program.programName}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        program.status === 'Finalist' 
                          ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                      }`}>
                        {program.status}
                      </span>
                      <span className="text-xs text-slate-600 dark:text-slate-400">
                        Stage: {program.stage}
                      </span>
                      <span className="text-xs text-slate-600 dark:text-slate-400">
                        {program.date}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-slate-600 dark:text-slate-400">Score:</span>
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                      {program.technicalScore}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-700 dark:text-slate-300">
                  {program.reason}
                </p>
                <div className="mt-2 flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs">
                  <span>View full evaluation</span>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
            
            {/* Empty state if no past considerations */}
            {/* <div className="text-center py-8 text-slate-500 dark:text-slate-400 text-sm">
              No previous program considerations
            </div> */}
          </div>
        </Card>

        {showReviewerAssignment && programContext && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">
              Reviewer
            </h3>
            <div>
              <p className="text-xs text-slate-600 dark:text-slate-400 mb-2 uppercase tracking-wider">
                Assigned Reviewer
              </p>
              <Select value={selectedReviewer} onValueChange={setSelectedReviewer}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a reviewer..." />
                </SelectTrigger>
                <SelectContent>
                  {reviewers.map((reviewer) => (
                    <SelectItem key={reviewer} value={reviewer}>
                      {reviewer}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Card>
        )}
      </div>

      {/* Right Column */}
      <div className="space-y-6">
        {showSubmittedDocuments && programContext && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">
              Submitted Documents
            </h3>
            <div className="space-y-3">
              {[
                { name: 'Technical Proposal.pdf', size: '2.4 MB', date: 'Oct 22, 2025' },
                { name: 'Cost Breakdown.xlsx', size: '156 KB', date: 'Oct 22, 2025' },
                { name: 'Company Overview.pdf', size: '1.8 MB', date: 'Oct 21, 2025' },
                { name: 'Certifications.pdf', size: '892 KB', date: 'Oct 21, 2025' },
                { name: 'Past Performance.pdf', size: '3.1 MB', date: 'Oct 20, 2025' },
              ].map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <div>
                      <p className="text-sm text-slate-900 dark:text-slate-50">{file.name}</p>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        {file.size} • {file.date}
                      </p>
                    </div>
                  </div>
                  <ExternalLink className="h-4 w-4 text-slate-400" />
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Vendor Intelligence Section */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">
            Vendor Intelligence
          </h3>
          <div className="space-y-3">
            {[
              { 
                name: 'Autonomous Systems White Paper 2024.pdf', 
                source: 'Company Website',
                type: 'Research Paper',
                date: 'Sep 15, 2025',
                summary: 'Detailed technical overview of their navigation algorithms and sensor fusion approach'
              },
              { 
                name: 'Defense Tech Magazine Feature.pdf', 
                source: 'Defense Tech Quarterly',
                type: 'Press Coverage',
                date: 'Aug 30, 2025',
                summary: 'Profile highlighting successful field tests with Army Research Lab'
              },
              { 
                name: 'Patent: Adaptive Perception System.pdf', 
                source: 'USPTO',
                type: 'Patent',
                date: 'Jul 12, 2025',
                summary: 'Patent filing for GPS-denied navigation using terrain mapping'
              },
              { 
                name: 'LinkedIn Company Analysis.pdf', 
                source: 'OSINT Collection',
                type: 'Intelligence',
                date: 'Oct 10, 2025',
                summary: 'Recent hires include 3 ex-Boeing engineers and 2 DARPA researchers'
              },
            ].map((doc, idx) => (
              <div
                key={idx}
                className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-8 h-8 rounded bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-50">
                        {doc.name}
                      </p>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 whitespace-nowrap flex-shrink-0">
                        {doc.type}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                      Source: {doc.source} • {doc.date}
                    </p>
                    <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                      {doc.summary}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

